import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import SearchIcon from "@mui/icons-material/Search";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BuildProjectGoalAchievementListPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const project = await fetchProject(projectId);
      setProjectName(project.projectName || "");

      const goals = project.goals || [];
      const achievements = project.achievements || [];

      // 目標ごとに最新の実績を紐付け
      const combined = goals.map((goal, idx) => {
        const indicatorName = goal.indicator?.indicatorName || "";
        // 同じ指標名の実績を絞り込み、最新（actualDate降順）を取得
        const matched = achievements
          .filter((a) => a.goal?.indicator?.indicatorName === indicatorName)
          .sort((a, b) => {
            if (!a.actualDate && !b.actualDate) return 0;
            if (!a.actualDate) return 1;
            if (!b.actualDate) return -1;
            return new Date(b.actualDate) - new Date(a.actualDate);
          });
        const latest = matched[0] || null;
        return {
          id: idx,
          indicatorName,
          unit: goal.indicator?.unit || "",
          plannedValue: goal.plannedValue ?? "",
          plannedDate: goal.plannedDate || "",
          deadline: goal.deadline || "",
          actualValue: latest?.actualValue ?? "",
          actualDate: latest?.actualDate || "",
          achievementCount: matched.length,
        };
      });

      setRows(combined);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) => r.indicatorName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "indicatorName", headerName: t("project.goal.indicator"), flex: 1, minWidth: 160 },
    { field: "unit",          headerName: t("project.goal.unit"),      width: 80 },
    { field: "plannedValue",  headerName: t("project.goal.plannedValue"), width: 110 },
    { field: "plannedDate",   headerName: t("project.goal.plannedDate"),  width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "deadline",      headerName: t("project.goal.deadline"),     width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "actualValue",   headerName: t("project.achievement.actualValue"), width: 110 },
    { field: "actualDate",    headerName: t("project.achievement.actualDate"),  width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "achievementCount", headerName: t("project.goalAchievement.count"), width: 90,
      type: "number" },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t("project.goalAchievement.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {projectName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {projectName}
          </Typography>
        )}
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
      />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/members`)}
        >
          {t("common.back")}
        </Button>
      </Box>
    </Box>
  );
}
